import { html, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { InputEvents, InputWidthValues } from '../types';
import { GzInputWebComponent } from './GzInputWebComponent';
import { debounce } from '../../../utils/event.utils';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { classMap } from 'lit/directives/class-map.js';
import { styles as numberInputStyles } from '../view/input-number.scss';
import { Keys } from '../../../types/common/types';
import { ButtonColor } from '../../gzIconButton/types';

@customElement('gz-input-number')
export class GzInputNumberWebComponent extends GzInputWebComponent<number> {
  static get styles() {
    return [...GzInputWebComponent.styles, unsafeCSS(`${numberInputStyles}`)];
  }
  @property({ type: Number, attribute: true, reflect: true })
  value: number | undefined;

  /**
   * The minimum value allowed in the input
   */
  @property({ type: Number, attribute: true, reflect: true })
  min: number;

  /**
   * The maximum value allowed in the input
   */
  @property({ type: Number, attribute: true, reflect: true })
  max: number;

  /**
   * If this is set, the number will be filled with 0s in the beginning
   * until the minimum required length is reached. This is only
   *
   * Examples:
   * - minLength = 3, value = 2    ==> 002
   * - minLength = 3, value = 17   ==> 017
   * - minLength = 3, value = 1024 ==> 1024
   */
  @property({ type: Number, attribute: true, reflect: true })
  minLength?: number;

  /**
   * If true, if max value is reached in the input, incrementing the input will revert the value
   * to min. Similarly, if min value is reached, on input decrement, the value will revert to max
   *
   * NOTE: This has an effect only when both min and max are set, otherwise it is ignored.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  circular?: boolean;

  @property({ type: String, attribute: true, reflect: true })
  unitPrefix? = '';

  @property({ type: String, attribute: true, reflect: true })
  unitSuffix? = '';

  /**
   * Set this property to TRUE to enable the search feature in the component
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  searchOnAction = false;

  /**
   * The canDelete property controls the CLEAR (X) / SEARCH buttons visibility in the input, if searchOnAction = true
   */
  @state()
  private canDelete = false;

  /**
   * This property is used to keep track of the first update on the `value` property, when the input is
   * searchable (`searchOnAction` property is set to TRUE). The clear button should be active be displayed only when the
   * component first receives a new value. On the next updates of the `value` property, the X icon should switch
   * to SEARCH.
   */
  @state()
  private valueChanged = false;

  /**
   * The default number input width is small
   */
  inputWidth = InputWidthValues.SMALL;

  private static getValueWithLeadingZeros(stringValue: string, minLength): string {
    const pad = minLength - stringValue.length < 0 ? 0 : minLength - stringValue.length;
    return `${'0'.repeat(pad)}${stringValue}`;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.updateClearButtonVisibility();
  }

  updated(properties: PropertyValues): void {
    if (properties.has('value')) {
      if (!isEmpty(this.value)) {
        const restrictedValue = this.getValueRestrictedToMinMax(this.value as number);
        this.inputElement.value = this.getInputElementValueWithMinLength(restrictedValue);
      } else {
        this.inputElement.value = '';
      }

      this.updateClearButtonVisibility();
    }
  }

  render() {
    const classes = {
      input: true,
      'input--number': true,
      invalid: this.invalid,
      'floating-label': !isEmpty(this.inputElement?.value),
      'has-label': !isEmpty(this.label),
    };

    return html`
      <div class="${classMap(classes)}">
        <input
          class="input--field"
          type="number"
          id="input--field"
          ?disabled="${this.disabled}"
          ?readonly="${this.readonly}"
          .placeholder="${this.placeholder}"
          .min="${this.min}"
          .max="${this.max}"
          @input="${this.handleTypingInNativeInput}"
          @keyup="${this.handleKeyUp}"
        />
        ${this.renderInvalidIcon()} ${this.renderSuccessIcon()} ${this.renderInputLabel()} ${this.renderUnitElement()}
        ${this.renderInputElements()}
      </div>
    `;
  }

  protected renderUnitElement(): TemplateResult | undefined {
    if (isEmpty(this.unitPrefix) && isEmpty(this.unitSuffix)) {
      return;
    }

    const classes = {
      unit: true,
      'unit-left': !!this.unitPrefix,
    };

    const value = this.unitPrefix || this.unitSuffix;

    return html`<label tabindex="-1" class="${classMap(classes)}" for="input--field">${value}</label>`;
  }

  private handleKeyUp(event: KeyboardEvent) {
    if (!this.searchOnAction) {
      return;
    }

    const debounceSearchOnEnter = debounce(() => {
      if (event.key === Keys.Enter) {
        this.onInputSearch();
      }
    }, 300);

    debounceSearchOnEnter();
  }

  protected handleTypingInNativeInput() {
    // Delay the string formatting with leading 0's and the component value update until the user finishes typing.
    // If the event is not debounced, each time the user inputs a single digit, the value will be immediately
    // formatted and the user will not be able to add a second digit.
    const debouncedInputFormattingEvent = debounce(() => {
      let displayedInputValue: number | undefined;

      if (this.inputElement.value === '') {
        // input value is empty and no min value defines => input value should be empty
        if (isEmpty(this.min)) {
          displayedInputValue = undefined;
        } else {
          // input value is empty, but min is defined => input value should revert to min
          displayedInputValue = this.getValueRestrictedToMinMax(0);
        }

        // we have value, but we need to check if it needs to be restricted
      } else {
        displayedInputValue = this.getValueRestrictedToMinMax(parseInt(this.inputElement.value, 10));
      }

      this.inputElement.value = !isEmpty(displayedInputValue)
        ? this.getInputElementValueWithMinLength(displayedInputValue)
        : '';
      this.onUserInput();
    }, 300);
    debouncedInputFormattingEvent();
  }

  protected setInputElementValue(value?: number) {
    if (value === null || value === undefined) {
      this.inputElement.value = '';
    } else {
      this.inputElement.value = this.getInputElementValueWithMinLength(value);
    }
  }

  protected onUserInput() {
    this.setActive(true);
    this.value = this.inputElement.value ? parseInt(this.inputElement.value, 10) : undefined;
    this.setCanDelete(false);
    this.emitInputEvent(InputEvents.CHANGE);
  }

  protected renderInputElements() {
    return html`
      ${this.renderSearchElements()}
      <div class="number-control" part="numberControl">
        ${this.renderIncrementButton()} ${this.renderDecrementButton()}
      </div>
    `;
  }

  private renderIncrementButton() {
    return html` <button @click="${this.incrementValue}" class="button number-control--arrow increment">
      <gz-icon name="ds-icon-textbox_increase" size="20" color="${ButtonColor.SECONDARY}"></gz-icon>
    </button>`;
  }

  private renderDecrementButton() {
    return html` <button @click="${this.decrementValue}" class="button number-control--arrow decrement">
      <gz-icon name="ds-icon-textbox_decrease" size="20" color="${ButtonColor.SECONDARY}"></gz-icon>
    </button>`;
  }

  private incrementValue() {
    // default state: circular = false, min || max = undefined

    // input is initially empty, value = '', the input should first display the minimum value or 0
    if (!this.inputElement.value) {
      const inputDefaultValue = this.min ? this.min : 0;
      this.inputElement.value = this.getInputElementValueWithMinLength(inputDefaultValue);
      this.onUserInput();
      return;
    }

    // if circular and max limit is reached
    if (this.circular && !isEmpty(this.max) && parseInt(this.inputElement.value, 10) === this.max) {
      const inputDefaultValue = this.min ? this.min : 0;
      this.inputElement.value = this.getInputElementValueWithMinLength(inputDefaultValue);
      this.onUserInput();
      return;
    }

    this.inputElement.stepUp();
    this.inputElement.value = this.getInputElementValueWithMinLength(parseInt(this.inputElement.value, 10));
    this.onUserInput();
  }

  private decrementValue() {
    if (!this.inputElement.value) {
      const inputDefaultValue = this.min ? this.min : 0;
      this.inputElement.value = this.getInputElementValueWithMinLength(inputDefaultValue);
      this.onUserInput();
      return;
    }

    // if circular = true and min limit is reached
    if (this.circular && !isEmpty(this.min) && parseInt(this.inputElement.value, 10) === this.min) {
      const inputDefaultValue = this.max ? this.max : 0;
      this.inputElement.value = this.getInputElementValueWithMinLength(inputDefaultValue);
      this.onUserInput();
      return;
    }

    this.inputElement.stepDown();
    this.inputElement.value = this.getInputElementValueWithMinLength(parseInt(this.inputElement.value, 10));
    this.onUserInput();
  }

  private getInputElementValueWithMinLength(value: number | undefined): string {
    const val = value === undefined ? '' : value;

    return this.minLength
      ? GzInputNumberWebComponent.getValueWithLeadingZeros(val.toString(), this.minLength)
      : val.toString();
  }

  private getValueRestrictedToMinMax(val: number): number {
    if (this.circular) {
      if (isEmpty(this.min) || isEmpty(this.max)) {
        return val;
      }

      if (val > this.max) {
        return this.min;
      }
      if (val < this.min) {
        return this.max;
      }
    } else {
      if (!isEmpty(this.min) && val < this.min) {
        return this.min;
      }

      if (!isEmpty(this.max) && val > this.max) {
        return this.max;
      }
    }

    return val;
  }

  protected emitInputEvent(eventName: string) {
    const event = new CustomEvent(eventName, {
      detail: {
        sourceEvent: this.value,
      },
      bubbles: true,
      cancelable: true,
      composed: false,
    });
    this.dispatchEvent(event);
  }

  private renderSearchElements(): TemplateResult | undefined {
    if (!this.searchOnAction) {
      return;
    }
    return this.canDelete ? this.renderClearButton() : this.renderSearchButton();
  }

  private renderSearchButton(): TemplateResult {
    return html`
      <gz-icon-button
        id="searchIcon"
        @click="${this.onInputSearch}"
        class="input--button input--search"
        icon="ds-icon-search"
        size="24"
        fontsize="20"
        buttontype="square"
        buttonfill="quiet"
        buttoncolor="secondary"
      >
      </gz-icon-button>
    `;
  }

  protected onInputSearch() {
    this.inputElement.blur();
    this.emitInputEvent(InputEvents.SEARCH);
    this.setCanDelete(true);
  }

  protected setCanDelete(value: boolean) {
    this.canDelete = value;
  }

  public clearInputValue(e: Event) {
    e.preventDefault();
    this.inputElement.value = this.min ? this.getInputElementValueWithMinLength(this.min) : '';
    this.onUserInput();
    if (this.searchOnAction) {
      this.emitInputEvent(InputEvents.SEARCH);
    }
    this.inputElement.focus();
  }

  private updateClearButtonVisibility() {
    if (!this.searchOnAction) {
      return;
    }

    if (!this.valueChanged) {
      this.setCanDelete(!isEmpty(this.value));
      this.valueChanged = true;
    }
  }

  public forceUpdateClearButtonVisibility() {
    if (!this.searchOnAction) {
      return;
    }

    this.valueChanged = false;
    this.setCanDelete(!isEmpty(this.value));
  }
}
